/**
 * Presentational view for the owner scheduled-actions list (S08 —
 * COP-087 / COP-076 / COP-075). Split out of `./List` so the schedules
 * list markup is renderable under the admin's `renderToStaticMarkup`
 * test convention: this file is props-only and MUST NOT import the
 * HubClient singleton module or the two hub-calling dialog modules (the
 * schedule form + run-history dialogs) — they construct a HubClient at
 * module load that throws in the test env (documented in
 * `list-badge.test.tsx`). All data + handlers arrive as props from the
 * `useSchedulesList` hook in `./List`.
 */
import type {
  NotificationOwnerDto,
  ScheduleOwnerDto,
  ScheduleRunSummaryDto,
  TfTrackRow,
} from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert } from '@/components/ui/alert';
import {
  formatScheduleDateTime,
  getScheduleStateLabel,
  safeRunSummary,
  truncateId,
  type ScheduleEnabledFilter,
} from './schedule-ui-model';
import {
  ScheduleNotificationInline,
  ScheduleNotifications,
} from './ScheduleNotifications';
import { CreatedInChatBadge } from './created-in-chat-badge';

export interface ScheduleListRow {
  schedule: ScheduleOwnerDto;
  latestRun: ScheduleRunSummaryDto | null;
}

export interface BusyAction {
  id: string;
  action: 'pause' | 'resume' | 'delete';
}

const ENABLED_FILTER_OPTIONS: { value: ScheduleEnabledFilter; label: string }[] = [
  { value: 'all', label: 'All states' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'paused', label: 'Paused' },
];

export interface SchedulesListViewProps {
  enabledFilter: ScheduleEnabledFilter;
  setEnabledFilter: (value: ScheduleEnabledFilter) => void;
  trackIdFilter: string;
  setTrackIdFilter: (value: string) => void;
  rows: ScheduleListRow[];
  tracks: TfTrackRow[];
  trackById: Map<string, TfTrackRow>;
  notifications: NotificationOwnerDto[];
  isLoading: boolean;
  listError: string | null;
  actionError: string | null;
  tracksWarning: string | null;
  notificationsWarning: string | null;
  busyAction: BusyAction | null;
  openCreateDialog: () => void;
  refresh: () => void;
  openEditDialog: (row: ScheduleListRow) => void;
  toggleEnabled: (row: ScheduleListRow) => void;
  setHistoryTarget: (row: ScheduleListRow) => void;
  setDeleteTarget: (row: ScheduleListRow) => void;
}

export function SchedulesListView({
  enabledFilter,
  setEnabledFilter,
  trackIdFilter,
  setTrackIdFilter,
  rows,
  tracks,
  trackById,
  notifications,
  isLoading,
  listError,
  actionError,
  tracksWarning,
  notificationsWarning,
  busyAction,
  openCreateDialog,
  refresh,
  openEditDialog,
  toggleEnabled,
  setHistoryTarget,
  setDeleteTarget,
}: SchedulesListViewProps) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Scheduled actions</h1>
          <p className="text-sm text-muted-foreground">
            Owner-scoped scheduled agent actions. Pause, resume, and soft-delete only the schedules returned by the Hub.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={openCreateDialog} disabled={isLoading}>
            Create schedule
          </Button>
          <Button size="sm" variant="outline" onClick={() => void refresh()} disabled={isLoading}>
            {isLoading ? 'Loading…' : 'Refresh'}
          </Button>
        </div>
      </div>

      <ScheduleNotifications notifications={notifications} warning={notificationsWarning} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>List is capped at 100 owner-owned schedules; no client-side owner filtering is applied.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label htmlFor="schedules-state" className="text-xs text-muted-foreground">
                State
              </label>
              <select
                id="schedules-state"
                className="h-9 rounded-md border bg-background px-3 py-1 text-sm"
                value={enabledFilter}
                onChange={(event) => setEnabledFilter(event.target.value as ScheduleEnabledFilter)}
              >
                {ENABLED_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="schedules-track" className="text-xs text-muted-foreground">
                Track
              </label>
              <select
                id="schedules-track"
                className="h-9 min-w-64 rounded-md border bg-background px-3 py-1 text-sm"
                value={trackIdFilter}
                onChange={(event) => setTrackIdFilter(event.target.value)}
              >
                <option value="">All tracks</option>
                {tracks.map((track) => (
                  <option key={track.id} value={track.id}>{track.name}</option>
                ))}
              </select>
            </div>
          </div>
          {tracksWarning ? (
            <p role="status" className="mt-3 text-sm text-muted-foreground">{tracksWarning}</p>
          ) : null}
        </CardContent>
      </Card>

      {listError ? <Alert>{listError}</Alert> : null}
      {actionError ? <Alert>{actionError}</Alert> : null}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>State</TableHead>
            <TableHead>Track</TableHead>
            <TableHead>Schedule</TableHead>
            <TableHead>Timing</TableHead>
            <TableHead>Preapproved</TableHead>
            <TableHead>Latest run</TableHead>
            <TableHead>Notifications</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center text-muted-foreground">Loading schedules…</TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center text-muted-foreground">
                {listError ? 'Schedules could not be loaded.' : 'No scheduled actions match the current filters.'}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const track = trackById.get(row.schedule.trackId);
              const state = getScheduleStateLabel(row.schedule.enabled);
              const isBusy = busyAction?.id === row.schedule.id;
              return (
                <TableRow
                  key={row.schedule.id}
                  // Plan 02-06 (D-26 / D-27 / EVAL-UX-02): stable per-row
                  // selector for Playwright (UI-SPEC line 741).
                  data-testid={`schedule-row-${row.schedule.id}`}
                >
                  <TableCell>
                    <Badge variant={state.badgeVariant}>{state.label}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-52">
                      <div className="truncate font-medium" title={track?.name ?? row.schedule.trackId}>
                        {track?.name ?? truncateId(row.schedule.trackId)}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground" title={row.schedule.trackId}>
                        {truncateId(row.schedule.trackId)}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <code className="font-mono text-xs">{row.schedule.scheduleExpr}</code>
                        {/*
                          Plan 02-06 (D-26 / D-28): provenance badge next to
                          the schedule identifier. Renders only on copilot-
                          created rows; admin rows render nothing. Tooltip
                          copy is D-26 verbatim; the badge re-uses the
                          existing shadcn <Badge variant="secondary"> (no
                          new shadcn block added).
                        */}
                        <CreatedInChatBadge createdBySource={row.schedule.createdBySource} />
                      </div>
                      <div className="text-xs text-muted-foreground">{row.schedule.timezone}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 text-xs">
                      <div>Next: {formatScheduleDateTime(row.schedule.nextRunAt)}</div>
                      <div className="text-muted-foreground">Last: {formatScheduleDateTime(row.schedule.lastRunAt)}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={row.schedule.wholePlanPreapproved ? 'default' : 'outline'}>
                      {row.schedule.wholePlanPreapproved ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <LatestRunCell latestRun={row.latestRun} lastRunAt={row.schedule.lastRunAt} />
                  </TableCell>
                  <TableCell>
                    <ScheduleNotificationInline notifications={notifications} scheduleId={row.schedule.id} />
                  </TableCell>
                  <TableCell>
                    <span className="text-xs">{formatScheduleDateTime(row.schedule.updatedAt)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isBusy}
                        onClick={() => setHistoryTarget(row)}
                        aria-label="View schedule run history"
                      >
                        Runs
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isBusy}
                        onClick={() => openEditDialog(row)}
                        aria-label="Edit schedule"
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isBusy}
                        onClick={() => void toggleEnabled(row)}
                        aria-label={row.schedule.enabled ? 'Pause schedule' : 'Resume schedule'}
                      >
                        {busyAction?.id === row.schedule.id && busyAction.action !== 'delete'
                          ? 'Working…'
                          : row.schedule.enabled ? 'Pause' : 'Resume'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={isBusy}
                        onClick={() => setDeleteTarget(row)}
                        aria-label="Delete schedule"
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <p className="text-xs text-muted-foreground">
        Showing {rows.length} schedule{rows.length === 1 ? '' : 's'} from the owner-scoped endpoint.
        {isLoading && rows.length > 0 ? ' Refreshing…' : ''}
      </p>
    </div>
  );
}

function LatestRunCell({ latestRun, lastRunAt }: { latestRun: ScheduleRunSummaryDto | null; lastRunAt: string | null }) {
  if (!latestRun) {
    return (
      <div className="space-y-1 text-xs text-muted-foreground">
        <div>Status: —</div>
        <div>Attempt: —</div>
        <div>{lastRunAt ? `Last run at ${formatScheduleDateTime(lastRunAt)}` : 'No recorded runs yet'}</div>
      </div>
    );
  }

  const run = safeRunSummary(latestRun);
  return (
    <div className="max-w-64 space-y-1 text-xs">
      <div className="flex flex-wrap items-center gap-1">
        <Badge variant={run.badgeVariant}>{run.statusLabel}</Badge>
        <span className="text-muted-foreground">Attempt {run.attemptCount}</span>
      </div>
      <div className="text-muted-foreground">{run.retryLabel}</div>
      <div className="truncate text-muted-foreground" title={run.errorLabel}>Error: {run.errorLabel}</div>
      <div className="font-mono text-muted-foreground" title={run.traceLabel}>Trace: {truncateId(run.traceLabel)}</div>
    </div>
  );
}
